import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { apiUrl } from '../core/api-client';
import { PlatformApi, PublicIqVerification } from '../core/platform-api';
import { LocalizationService } from '../core/localization.service';
import { TranslatePipe } from '../core/translate.pipe';
import {
  DsAlertComponent,
  DsBadgeComponent,
  DsButtonDirective,
  DsCardDirective,
  DsProgressComponent,
  DsScoreVisualizationComponent,
  DsStateComponent,
} from '../ui';

@Component({
  selector: 'app-iq-verification-page',
  imports: [
    RouterLink,
    TranslatePipe,
    DsAlertComponent,
    DsBadgeComponent,
    DsButtonDirective,
    DsCardDirective,
    DsProgressComponent,
    DsScoreVisualizationComponent,
    DsStateComponent,
  ],
  templateUrl: './iq-verification-page.html',
  styleUrl: './iq-verification-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IqVerificationPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(PlatformApi);
  private readonly i18n = inject(LocalizationService);
  protected readonly verification = signal<PublicIqVerification | null>(null);
  protected readonly loading = signal(true);
  protected readonly notFound = signal(false);

  ngOnInit(): void {
    void this.load();
  }

  protected completedDate(value: string, _language: string): string {
    return new Intl.DateTimeFormat(this.i18n.locale(), {
      dateStyle: 'long',
      timeZone: 'UTC',
    }).format(new Date(value));
  }

  protected birthDate(value: string | null): string {
    if (!value) return this.i18n.t('verify.notAvailable');
    return new Intl.DateTimeFormat(this.i18n.locale(), {
      dateStyle: 'long',
      timeZone: 'UTC',
    }).format(new Date(`${value}T00:00:00Z`));
  }

  protected domainName(domain: string, _language: string): string {
    return this.i18n.domain(domain);
  }

  protected certificateUrl(testId: string): string {
    return apiUrl(`/api/v1/public-iq-tests/${testId}/certificate.pdf`);
  }

  private async load(): Promise<void> {
    const testId = this.route.snapshot.paramMap.get('testId');
    if (!testId) {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }
    try {
      this.verification.set(await this.api.getPublicIqVerification(testId));
    } catch {
      this.notFound.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
